import "./App.css";
import React, {useEffect, useState} from "react";
import {BrowserRouter as Router, Routes, Route, Navigate} from "react-router-dom";
import { HelmetProvider } from 'react-helmet-async';
import Header from "./components/Partials/Header";
import HomeHeader from "./components/Partials/HomeHeader";
import PaymentSuccessHeader from "./components/Partials/PaymentSuccessHeader";
import Home from "./components/Home/Home";
import Footer from "./components/Partials/Footer";
import UploadImage from "@pages/UploadImage";
import ReviewImages from "@pages/ReviewImages";
import GalleryView from "@pages/GalleryView";
import PersonalArea from "@pages/PersonalArea";
import BusinessGifts from "@pages/BusinessGifts";
import BusinessGiftsHello from "@pages/BusinessGiftsHello";
import GiftCard from "@pages/GiftCard";
import GiftSuccess from "@pages/GiftSuccess";
import WallProduct from "@pages/WallProduct";
import ProductsCatalog from "@pages/ProductsCatalog";
import PaymentSuccess from "./components/UploadImage/PaymentSuccess.js";
import Overlay from "@shared/Overlay";
import SecondOverlay from "@shared/SecondOverlay";
import WindowModals from "@shared/WindowModals";
import SecondaryModals from "@shared/SecondaryModals";
import PaymentStatusSnackbar from "@shared/components/PaymentStatusSnackbar";
import {RecoilRoot, useSetRecoilState} from "recoil";
import RecoilNexus from "recoil-nexus";
import {getUserImages} from "@api";
import { getPrices } from "./components/api/pricingService";
import { getShippingPrice } from "./components/api/shippingService";
import { getDiscountConfig } from "./components/api/discountConfigService";
import { trackVisit } from "./components/api/visitTracking";
import { secondaryModals, secondOverlayState } from "./components/Recoil/Atoms";
import { framePricesState, defaultFramePriceState, shippingPriceState, discountConfigState } from "./components/Recoil/Atoms/priceCalc";
import PaymentRedirector from "./components/PaymentRedirector";

import "slick-carousel/slick/slick.css";
import "slick-carousel/slick/slick-theme.css";

// Component to fetch and set frame prices
const PricingSetup = () => {
  const setFramePrices = useSetRecoilState(framePricesState);
  const setDefaultFramePrice = useSetRecoilState(defaultFramePriceState);

  useEffect(() => {
    // Fetch prices when the app initializes
    const fetchPrices = async () => {
      try {
        const prices = await getPrices();
        if (prices && prices.length > 0) {
          const priceMap = {};
          prices.forEach(item => {
            priceMap[item.frameType] = item.price;
          });
          setFramePrices(priceMap);
        }
      } catch (error) {
        console.error('Error fetching frame prices:', error);
        // If there's an error, we'll use the default price of 49
        setDefaultFramePrice(49);
      }
    };
    
    fetchPrices();
  }, [setFramePrices, setDefaultFramePrice]);

  return null;
};

// Component to fetch and set shipping price
const ShippingSetup = () => {
  const setShippingPrice = useSetRecoilState(shippingPriceState);

  useEffect(() => {
    // Fetch shipping price when the app initializes
    const fetchShippingPrice = async () => {
      try {
        const shipping = await getShippingPrice();
        if (shipping && shipping.price) {
          setShippingPrice(shipping.price);
        }
      } catch (error) {
        console.error('Error fetching shipping price:', error);
        // If there's an error, we'll use the default price of 35
        setShippingPrice(35);
      }
    };

    fetchShippingPrice();
  }, [setShippingPrice]);

  return null;
};

// Helper to apply discount config colors to CSS variables
const applyDiscountColors = (colors) => {
  if (!colors) return;
  const root = document.documentElement;
  root.style.setProperty('--discount-tag-bg', colors.tagBackground);
  root.style.setProperty('--discount-tag-text', colors.tagText);
  root.style.setProperty('--discounted-price-color', colors.discountedPrice);
  root.style.setProperty('--bundle-badge-bg', colors.bundleBadgeBackground);
};

// Apply cached colors immediately on script load (before React renders)
try {
  const cached = localStorage.getItem('discountConfig');
  if (cached) {
    const config = JSON.parse(cached);
    applyDiscountColors(config.colors);
  }
} catch (e) {
  // Ignore errors
}

// Component to fetch and set discount configuration
const DiscountConfigSetup = () => {
  const setDiscountConfig = useSetRecoilState(discountConfigState);

  useEffect(() => {
    // Fetch discount config when the app initializes
    const fetchDiscountConfig = async () => {
      try {
        const config = await getDiscountConfig();
        if (config) {
          setDiscountConfig(config);

          // Cache config in localStorage to prevent flash on next load
          try {
            localStorage.setItem('discountConfig', JSON.stringify(config));
          } catch (e) {
            // Ignore storage errors
          }

          // Apply colors from fresh config
          applyDiscountColors(config.colors);
        }
      } catch (error) {
        console.error('Error fetching discount config:', error);
      }
    };

    fetchDiscountConfig();
  }, [setDiscountConfig]);

  return null;
};

// Records a unique visitor once per app load for conversion-rate (CRO) metrics.
const VisitTracker = () => {
  useEffect(() => {
    trackVisit();
  }, []);

  return null;
};

function App() {
  const [existData, setExistData] = useState(false);

  useEffect(() => {
    getUserImages().then(({data}) => {
      setExistData(false);
      if (data.data.length) {
        setExistData(true);
        localStorage.setItem("dataExist", 1);
      } else {
        localStorage.setItem("dataExist", 0);
        setExistData(false);
      }
    });
    if (!localStorage.getItem("frameSelected")) {
      localStorage.setItem("frameSelected", "ever");
    }
  }, []);

  return (
    <RecoilRoot>
      <HelmetProvider>
        <RecoilNexus />
        <PricingSetup />
        <ShippingSetup />
        <DiscountConfigSetup />
        <VisitTracker />
        <Router>
          <Routes>
            <Route path="/" element={
              <>
                <HomeHeader />
                <div className="page-content-wrapper">
                  <PaymentRedirector />
                  <Home existData={existData} />
                </div>
                <Footer existData={existData} />
              </>
            } />
            <Route path="/upload-your-image" element={
              <>
                <div className="page-content-wrapper">
                  <PaymentRedirector />
                  <Header />
                  <UploadImage />
                </div>
              </>
            } />
            <Route path="/review-your-images" element={
              <>
                <div className="page-content-wrapper">
                  <PaymentRedirector />
                  <Header />
                  <ReviewImages preloadedData={null} />
                </div>
              </>
            } />
            <Route path="/payment-success/:orderId" element={
              <>
                <PaymentSuccessHeader />
                <div className="page-content-wrapper">
                  <PaymentRedirector />
                  <PaymentSuccess />
                </div>
              </>
            } />
            <Route path="/gallery/:sessionId" element={
              <>
                <div className="page-content-wrapper">
                  <PaymentRedirector />
                  <Header />
                  <GalleryView />
                </div>
              </>
            } />
            <Route path="/personal-area" element={
              <>
                <div className="page-content-wrapper">
                  <Header />
                  <PersonalArea />
                </div>
              </>
            } />
            <Route path="/gift-card" element={
              <>
                <div className="page-content-wrapper">
                  <PaymentRedirector />
                  <Header />
                  <div className="gc-page">
                    <GiftCard mode="page" />
                  </div>
                </div>
              </>
            } />
            <Route path="/gift-success/:id" element={
              <>
                <PaymentSuccessHeader />
                <div className="page-content-wrapper">
                  <GiftSuccess />
                </div>
              </>
            } />
            <Route path="/business-gifts/hello" element={
              <>
                <div className="page-content-wrapper">
                  <BusinessGiftsHello />
                </div>
              </>
            } />
            <Route path="/business-gifts/:businessCode" element={
              <>
                <div className="page-content-wrapper">
                  <BusinessGifts />
                </div>
              </>
            } />
            <Route path="/products" element={
              <>
                <div className="page-content-wrapper">
                  <PaymentRedirector />
                  <Header />
                  <ProductsCatalog />
                </div>
              </>
            } />
            <Route path="/products/:bundle" element={
              <>
                <div className="page-content-wrapper">
                  <PaymentRedirector />
                  <Header />
                  <WallProduct />
                </div>
              </>
            } />
            <Route path="/products/:bundle/:wallId" element={
              <>
                <div className="page-content-wrapper">
                  <PaymentRedirector />
                  <Header />
                  <WallProduct />
                </div>
              </>
            } />
            <Route path="*" element={<Navigate to="/" />} />
          </Routes>
          <WindowModals />
          <SecondaryModals />
          {/* Bit payment result - global so it survives the route change to /payment-success */}
          <PaymentStatusSnackbar />
        </Router>
        <Overlay />
        <SecondOverlay />
      </HelmetProvider>
    </RecoilRoot>
  );
}

export default App;
